import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, MessageSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../../services/api';
import Modal from '../../../components/shared/Modal';

const TIPO_LABELS = {
  mudanca_estagio: 'Mudança de estágio',
  recontato_automatico: 'Recontato automático',
  reaquecido_automatico: 'Reaquecido',
  nota_manual: 'Nota',
};

function labelParaTipo(tipo) {
  return TIPO_LABELS[tipo] || tipo;
}

function resumoInteracao(interacao) {
  const p = interacao.payload || {};
  if (interacao.tipo === 'mudanca_estagio' && p.de && p.para) return `${p.de} → ${p.para}${p.motivo ? ` (${p.motivo})` : ''}`;
  if (p.descricao) return p.descricao;
  if (p.resumo) return p.resumo;
  return null;
}

export default function InteracoesTimeline({ corretorId }) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [tipo, setTipo] = useState('nota_manual');
  const [descricao, setDescricao] = useState('');

  const { data: interacoes, isLoading } = useQuery({
    queryKey: ['parceiro-interacoes', corretorId],
    queryFn: () => api.get(`/parceiros/${corretorId}/interacoes`).then(r => r.data.data),
  });

  const registrarMut = useMutation({
    mutationFn: () => api.post(`/parceiros/${corretorId}/interacoes`, { tipo, descricao: descricao || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parceiro-interacoes', corretorId] });
      setModalOpen(false);
      setTipo('nota_manual');
      setDescricao('');
    },
  });

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-700">Histórico de interações</h2>
        <button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary"
        >
          <Plus size={15} /> Registrar interação
        </button>
      </div>

      {isLoading && <div className="text-center text-gray-400 py-6 text-sm">Carregando...</div>}
      {!isLoading && (interacoes || []).length === 0 && (
        <div className="text-center text-gray-400 py-6 text-sm">Nenhuma interação registrada ainda.</div>
      )}
      {!isLoading && (interacoes || []).length > 0 && (
        <div className="space-y-2">
          {interacoes.map(i => (
            <div key={i.id} className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              <MessageSquare size={16} className="text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-800">{labelParaTipo(i.tipo)}</span>
                  {i.origem && <span className="text-xs text-gray-400">({i.origem})</span>}
                </div>
                {resumoInteracao(i) && <p className="text-sm text-gray-600 mt-0.5">{resumoInteracao(i)}</p>}
                <p className="text-xs text-gray-400 mt-1">
                  {i.registrado_por_nome ? `Registrado por ${i.registrado_por_nome} · ` : ''}
                  {formatDistanceToNow(new Date(i.created_at), { locale: ptBR, addSuffix: true })}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Registrar interação" size="sm">
        <form onSubmit={e => { e.preventDefault(); registrarMut.mutate(); }} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Tipo</label>
            <select value={tipo} onChange={e => setTipo(e.target.value)} className="input-field w-full">
              <option value="nota_manual">Nota</option>
              <option value="ligacao">Ligação</option>
              <option value="reuniao">Reunião</option>
              <option value="mensagem_manual">Mensagem enviada manualmente</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Descrição</label>
            <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={3} className="input-field w-full resize-y" placeholder="O que aconteceu nesse contato..." />
          </div>
          {registrarMut.isError && (
            <p className="text-sm text-red-600">{registrarMut.error?.response?.data?.error || 'Erro ao registrar interação'}</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button type="submit" disabled={registrarMut.isPending} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
              {registrarMut.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
